/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Close } from '@mui/icons-material'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'

import useT from '@/i18n/useT'

type DetectionHelpDialogProps = {
  open: boolean
  onClose: () => void
}

function DetectionHelpDialog({ open, onClose }: DetectionHelpDialogProps) {
  const { t } = useT()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t('detection.helpDialog.title', 'How Detection Works')}
        <IconButton
          aria-label={t(
            'detection.helpDialog.close.ariaLabel',
            'Close detection help',
          )}
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detection.helpDialog.instructions.title', 'Instructions')}
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.instructions.chooseLabel',
                  'Choose the active label in the left rail, then draw boxes for products, price tags, or empty facings.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.instructions.productTag',
                  'When the active label is Product, new boxes use the current active product tag.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.instructions.selection',
                  'Select one or more boxes, then use the buttons at the bottom-left of the image to apply the current label, apply the active tag, or delete the selection.',
                )}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detection.helpDialog.controls.title', 'Controls')}
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.controls.drawBox',
                  'Click and drag to draw a box. You can start a new box even from inside another box.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.controls.zoomPan',
                  'Zoom with the mouse wheel. Pan with right-click drag or while holding Control.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.controls.resetView',
                  'Use Reset view to return the image to its default framing.',
                )}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detection.helpDialog.interface.title', 'Interface')}
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.interface.hoverLabel',
                  'Hover a label on the left to isolate that class on the image. Use the eye icon to hide or show an entire class.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.interface.hoverBox',
                  'Hover a box to see its label. Selected boxes are highlighted on the image.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.interface.approve',
                  'Approve finishes the image once the detections are correct.',
                )}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detection.helpDialog.flagging.title', 'Flagging')}
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.flagging.flagImage',
                  'Flag image when the full image should leave the active workflow, such as the wrong aisle photo, a broken or unusable image, or a case where the image cannot be labeled reliably.',
                )}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t(
                  'detection.helpDialog.flagging.useFlagging',
                  'Use flagging instead of approval when the problem is with the image itself, not with individual boxes you can correct on this screen.',
                )}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default DetectionHelpDialog
